import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface MarketPrice {
  symbol: string;
  price: number;
  change_percent: number;
  volume: number;
  updated_at: string;
}

export function useMarketPrices(pollInterval = 5000) {
  const [prices, setPrices] = useState<MarketPrice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  const fetchPrices = useCallback(async () => {
    const { data, error } = await supabase
      .from('market_prices')
      .select('*')
      .order('symbol');

    if (!error && data && data.length > 0) {
      setPrices(data as MarketPrice[]);
      setLastUpdate(new Date());
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    fetchPrices();
    const interval = setInterval(fetchPrices, pollInterval);
    return () => clearInterval(interval);
  }, [fetchPrices, pollInterval]);

  const gainers = useMemo(() =>
    [...prices].filter(p => p.change_percent > 0).sort((a, b) => b.change_percent - a.change_percent),
    [prices]
  );

  const losers = useMemo(() =>
    [...prices].filter(p => p.change_percent < 0).sort((a, b) => a.change_percent - b.change_percent),
    [prices]
  );

  const mostActive = useMemo(() =>
    [...prices].sort((a, b) => Math.abs(b.change_percent) - Math.abs(a.change_percent)),
    [prices]
  );

  return { prices, gainers, losers, mostActive, isLoading, lastUpdate };
}
